import { type FC, useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PageTitle } from "../../../../components/PageTitle";
import { Button } from "../../../../components/ui/button";
import {
  type ImportResult,
  useContactsApi,
  useDeleteAllContactsApi,
  useDeleteContactApi,
  useExportContactsApi,
  useGroupsApi,
  useImportContactsApi,
} from "../../../../hooks/use-distribution-api";

export const ContactsPage: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(
    undefined,
  );

  const { data, loading, error, refetch } = useContactsApi({
    page,
    limit: 50,
    search: search || undefined,
    groupId: groupFilter || undefined,
    isActive: activeFilter,
  });
  const { data: groupsData } = useGroupsApi({ limit: 100 });
  const { deleteContact } = useDeleteContactApi();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // CSVインポート関連
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importGroupId, setImportGroupId] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { importContacts, loading: importLoading } = useImportContactsApi();

  // 全削除関連
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const { deleteAllContacts, loading: deleteAllLoading } =
    useDeleteAllContactsApi();

  // CSVエクスポート関連
  const { exportContacts, loading: exportLoading } = useExportContactsApi();

  const handleExport = async () => {
    try {
      await exportContacts();
      toast.success("CSVをダウンロードしました");
    } catch (e) {
      toast.error(
        `エクスポートエラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (contactId: string) => {
    setActionLoading(true);
    try {
      await deleteContact(contactId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  // CSVインポート関連ハンドラー
  const handleImportModalOpen = () => {
    setImportModalOpen(true);
    setImportFile(null);
    setImportGroupId("");
    setImportResult(null);
  };

  const handleImportModalClose = () => {
    if (!importLoading) {
      setImportModalOpen(false);
      setImportFile(null);
      setImportGroupId("");
      setImportResult(null);
    }
  };

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith(".csv")) {
      setImportFile(file);
      setImportResult(null);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      const result = await importContacts(
        importFile,
        importGroupId || undefined,
      );
      setImportResult(result);
      if (result.imported > 0 || result.updated > 0) {
        await refetch();
      }
    } catch (e) {
      alert(
        `インポートエラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const handleDeleteAll = async () => {
    try {
      const result = await deleteAllContacts();
      setDeleteAllModalOpen(false);
      toast.success(`${result.deleted}件の連絡先を削除しました`);
      await refetch();
    } catch (e) {
      toast.error(
        `削除エラー: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const totalPages = data ? Math.ceil(data.total / 50) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先管理</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>連絡先管理</PageTitle>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/distribution/groups")}
          >
            グループ管理
          </Button>
          <Button
            type="button"
            onClick={handleImportModalOpen}
            variant="secondary"
            size="sm"
          >
            CSVインポート
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={exportLoading || !data?.total}
            variant="outline"
            size="sm"
          >
            {exportLoading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              "CSVダウンロード"
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate("/admin/distribution/contacts/new")}
          >
            + 新規作成
          </Button>
        </div>
      </div>

      {/* フィルター */}
      <div className="mb-6 flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="会社名・担当者名・メールで検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered w-full md:w-72"
        />
        <select
          value={groupFilter}
          onChange={e => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
          className="select select-bordered"
        >
          <option value="">全てのグループ</option>
          {groupsData?.items.map(group => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <select
          value={activeFilter === undefined ? "" : String(activeFilter)}
          onChange={e => {
            const val = e.target.value;
            setActiveFilter(val === "" ? undefined : val === "true");
            setPage(1);
          }}
          className="select select-bordered"
        >
          <option value="">全ての状態</option>
          <option value="true">有効のみ</option>
          <option value="false">無効のみ</option>
        </select>
        <Button type="button" onClick={handleSearch}>
          検索
        </Button>
      </div>

      {/* テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>会社名</th>
                <th>担当者名</th>
                <th>メールアドレス</th>
                <th>状態</th>
                <th>所属グループ</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <span className="loading loading-spinner loading-md" />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-8 text-base-content/60"
                  >
                    連絡先がありません
                  </td>
                </tr>
              ) : (
                data?.items.map(contact => (
                  <tr key={contact.id} className="hover">
                    <td>
                      <Link
                        to={`/admin/distribution/contacts/${contact.id}`}
                        className="link link-primary font-medium"
                      >
                        {contact.companyName}
                      </Link>
                    </td>
                    <td>{contact.contactName || "-"}</td>
                    <td>{contact.email}</td>
                    <td>
                      <span
                        className={`badge badge-sm ${
                          contact.isActive ? "badge-success" : "badge-error"
                        }`}
                      >
                        {contact.isActive ? "有効" : "無効"}
                      </span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {contact.groups.slice(0, 3).map(group => (
                          <span
                            key={group.id}
                            className={`badge badge-sm ${
                              group.isDefault ? "badge-warning" : "badge-ghost"
                            }`}
                          >
                            {group.name}
                          </span>
                        ))}
                        {contact.groups.length > 3 && (
                          <span className="badge badge-sm badge-ghost">
                            +{contact.groups.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() =>
                            navigate(
                              `/admin/distribution/contacts/${contact.id}/edit`,
                            )
                          }
                        >
                          編集
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setDeleteConfirm(contact.id)}
                          variant="ghost"
                          size="xs"
                          className="text-error"
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              className="join-item"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button type="button" size="sm" className="join-item">
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              className="join-item"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* 全削除ボタン（目立たない位置に配置） */}
      {data && data.total > 0 && (
        <div className="flex justify-end mt-8 opacity-60 hover:opacity-100 transition-opacity">
          <Button
            type="button"
            onClick={() => setDeleteAllModalOpen(true)}
            variant="destructive"
            size="xs"
            isOutline
          >
            すべての連絡先を削除
          </Button>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">連絡先を削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(null)}
          />
        </div>
      )}

      {/* CSVインポートモーダル */}
      {importModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg mb-4">連絡先のCSVインポート</h3>

            {!importResult ? (
              <>
                {/* ファイルドロップエリア */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/10"
                      : "border-base-300 hover:border-primary"
                  }`}
                  onDragOver={e => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {importFile ? (
                    <div>
                      <p className="font-medium text-success">
                        {importFile.name}
                      </p>
                      <p className="text-sm text-base-content/60 mt-1">
                        クリックして別のファイルを選択
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">
                        CSVファイルをドラッグ＆ドロップ
                      </p>
                      <p className="text-sm text-base-content/60 mt-1">
                        または クリックしてファイルを選択
                      </p>
                    </div>
                  )}
                </div>

                {/* フォーマット説明 */}
                <div className="mt-4 text-sm text-base-content/70">
                  <p>
                    必須列: <code>company_name</code>, <code>email</code>
                  </p>
                  <p>
                    任意列: <code>contact_name</code>, <code>is_active</code>,{" "}
                    <code>notes</code>
                  </p>
                  <a
                    href="/import_csv_template.csv"
                    download
                    className="link link-primary text-sm mt-2 inline-block"
                  >
                    サンプルCSVをダウンロード
                  </a>
                </div>

                {/* グループ選択 */}
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">
                      インポート先グループ（任意）
                    </span>
                  </label>
                  <select
                    value={importGroupId}
                    onChange={e => setImportGroupId(e.target.value)}
                    className="select select-bordered"
                  >
                    <option value="">グループを指定しない</option>
                    {groupsData?.items.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* アクションボタン */}
                <div className="modal-action">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleImportModalClose}
                    disabled={importLoading}
                  >
                    キャンセル
                  </Button>
                  <Button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                  >
                    {importLoading ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      "インポート"
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* インポート結果 */}
                <div className="space-y-3">
                  <div className="stats stats-vertical lg:stats-horizontal shadow w-full">
                    <div className="stat">
                      <div className="stat-title">新規追加</div>
                      <div className="stat-value text-success">
                        {importResult.imported}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-title">更新</div>
                      <div className="stat-value text-info">
                        {importResult.updated}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-title">スキップ</div>
                      <div className="stat-value text-warning">
                        {importResult.skipped}
                      </div>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="alert alert-error">
                      <div>
                        <p className="font-medium">
                          エラー: {importResult.errors.length}件
                        </p>
                        <ul className="text-sm mt-2 list-disc list-inside max-h-32 overflow-y-auto">
                          {importResult.errors.map(err => (
                            <li key={err.row}>
                              行{err.row}: {err.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-action">
                  <Button type="button" onClick={handleImportModalClose}>
                    閉じる
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className="modal-backdrop" onClick={handleImportModalClose} />
        </div>
      )}

      {/* 全削除確認モーダル */}
      {deleteAllModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-error">
              すべての連絡先を削除しますか？
            </h3>
            <div className="py-4">
              <p className="text-base-content/80">
                この操作により、<strong>{data?.total}件</strong>
                の連絡先とグループへの関連付けがすべて削除されます。
              </p>
              <p className="text-error mt-2 text-sm font-medium">
                この操作は取り消せません。
              </p>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteAllModalOpen(false)}
                disabled={deleteAllLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteAll}
                disabled={deleteAllLoading}
              >
                {deleteAllLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "すべて削除"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !deleteAllLoading && setDeleteAllModalOpen(false)}
          />
        </div>
      )}
    </div>
  );
};
